import React, { useState } from 'react';
import { 
  X, 
  Mail, 
  Lock, 
  LogIn, 
  ShieldCheck, 
  AlertCircle, 
  Eye, 
  EyeOff, 
  Cloud, 
  ArrowRight, 
  UserCheck, 
  UserPlus, 
  ExternalLink, 
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import { 
  auth, 
  googleProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail,
  sendEmailVerification
} from '../lib/firebase';
import { APP_CONFIG, getAuthTitle, getDisplayProjectId } from '../config/appConfig';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (userEmail: string) => void;
  showToast: (msg: string) => void;
  onOpenTerms?: () => void;
  onOpenPrivacy?: () => void;
  initialEmail?: string;
}

export function AuthModal({ isOpen, onClose, onSuccess, showToast, onOpenTerms, onOpenPrivacy, initialEmail }: AuthModalProps) {
  const [authMethod, setAuthMethod] = useState<'google' | 'email'>(() => initialEmail ? 'email' : 'google');
  const [emailAuthMode, setEmailAuthMode] = useState<'signup' | 'signin'>('signup');

  // Email/Password state
  const [email, setEmail] = useState(() => initialEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isAgeVerified, setIsAgeVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleGoogleSignIn = async () => {
    if (!agreedToTerms || !isAgeVerified) {
      setError('You must agree to the Terms & Privacy Policy and certify you are at least 18 years of age to proceed.');
      return;
    }
    setError(null);
    setSuccessNotice(null);
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (result.user) {
        showToast(`Welcome, ${result.user.displayName || result.user.email}! 7-Day Free Trial active.`);
        onSuccess(result.user.email || 'Founder');
        onClose();
      }
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        console.info('Google sign-in popup was closed by user.');
        // Don't set error message on voluntary popup closure
      } else {
        console.error('Google Auth error:', err);
        if (err.code === 'auth/invalid-credential') {
          setError('Google Auth credential was invalid or expired. Please click "Continue with Google Sign-In" again or launch Local Vault.');
        } else if (err.code === 'auth/internal-error' || err.message?.includes('internal-error')) {
          setError('Google Auth popup encountered an internal environment restriction. Open in a new tab or use Email Sign Up / Sign In.');
        } else if (err.code === 'auth/popup-blocked') {
          setError('Google sign-in popup was blocked by browser. Please allow popups or use Email Sign Up / Sign In.');
        } else {
          setError(err.message || 'Failed to sign in with Google');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setError('You must agree to the Terms & Privacy Policy to proceed.');
      return;
    }
    if (emailAuthMode === 'signup' && !isAgeVerified) {
      setError('You must certify that you are at least 18 years of age to create an account.');
      return;
    }
    setError(null);
    setSuccessNotice(null);

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);

    try {
      if (emailAuthMode === 'signup') {
        const userCred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        const userEmail = userCred.user?.email || email.trim();

        // Trigger Firebase Email Verification for new signups
        if (userCred.user) {
          try {
            await sendEmailVerification(userCred.user);
            showToast(`Account created! Verification email sent to ${userEmail}. Please verify your inbox.`);
          } catch (verifErr: any) {
            console.warn('Email verification send notice:', verifErr);
            showToast(`Account created! Your 7-Day Free Trial is active for ${userEmail}.`);
          }
        } else {
          showToast(`Account created! Your 7-Day Free Trial is active for ${userEmail}.`);
        }

        onSuccess(userEmail);
        onClose();
        return;
      } else {
        const userCred = await signInWithEmailAndPassword(auth, email.trim(), password);
        const userEmail = userCred.user?.email || email.trim();
        showToast(`Welcome back, ${userEmail}!`);
        onSuccess(userEmail);
        onClose();
        return;
      }
    } catch (err: any) {
      console.error('Firebase Auth Error:', err?.code, err?.message);
      let msg = 'Authentication failed.';
      const cleanEmail = email.trim();

      if (err.code === 'auth/email-already-in-use') {
        msg = `An account with ${cleanEmail} already exists! Switched to "Sign In" mode — enter your password or click "Forgot Password?".`;
        setEmailAuthMode('signin');
      } else if (err.code === 'auth/invalid-email') {
        msg = `The email address '${cleanEmail}' is invalid. Please check for typos.`;
      } else if (err.code === 'auth/weak-password') {
        msg = 'Password is too weak. Please use at least 6 characters.';
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        if (emailAuthMode === 'signin') {
          msg = `Invalid password or email for ${cleanEmail}. Check your password, click "Forgot Password?" to reset, or switch to "Create Account".`;
        } else {
          msg = `Authentication notice for ${cleanEmail}: Invalid credentials. If you already created an account, switch to "Sign In" or click "Forgot Password?".`;
        }
      } else if (err.code === 'auth/user-not-found') {
        msg = `No account found for ${cleanEmail}. Switch to "Create Account" to start your 7-Day Free Trial.`;
      } else if (err.code === 'auth/operation-not-allowed') {
        msg = 'Email/Password sign-in is disabled in Firebase console. Please use Google Sign-In or launch Local Vault.';
      } else if (err.message) {
        msg = err.message;
      }

      showToast(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const cleanEmail = email.trim();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setError('Please enter your email address in the box first, then click "Forgot Password?".');
      return;
    }
    setError(null);
    setSuccessNotice(null);
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, cleanEmail);
      const msg = `Password reset link dispatched to ${cleanEmail}! Please check your Inbox and Spam/Junk folder.`;
      setSuccessNotice(msg);
      showToast(msg);
      setEmailAuthMode('signin');
    } catch (err: any) {
      console.error('Password Reset Error:', err?.code, err?.message);
      let msg = 'Failed to send password reset email.';
      if (err.code === 'auth/user-not-found') {
        msg = `No registered account found for ${cleanEmail}. Switch to "Create Account" to start your 7-Day Free Trial.`;
      } else if (err.code === 'auth/invalid-email') {
        msg = 'Invalid email address format. Please check for typos.';
      } else if (err.code === 'auth/missing-email') {
        msg = 'Please provide a valid email address.';
      } else if (err.message) {
        msg = err.message;
      }
      setError(msg);
      showToast(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2.5 sm:p-6 bg-black/80 backdrop-blur-md animate-fade-in overflow-y-auto">
      <div className="relative w-full max-w-lg my-auto max-h-[90vh] overflow-y-auto bg-[#151819] border border-[#2A2F31] rounded-3xl shadow-2xl text-white scrollbar-thin scrollbar-thumb-teal-500/20">
        
        {/* Glow Header */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-amber-500 to-emerald-500" />
        <div className="absolute -top-20 -right-20 w-48 h-48 bg-[#14B8A6]/10 rounded-full blur-2xl pointer-events-none" />

        {/* Modal Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white bg-[#0C0E0F]/50 hover:bg-[#0C0E0F] rounded-full border border-white/5 transition-all cursor-pointer z-10"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8 space-y-6">
          {/* Title & Badge */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 rounded-md bg-[#14B8A6]/10 border border-[#14B8A6]/30 text-[#14B8A6] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck size={13} /> Instant {APP_CONFIG.trialDays}-Day Free Trial Access
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold font-display tracking-tight text-white">
              Launch {getAuthTitle()}
            </h2>
          </div>

          {/* Prominent Trial Banner */}
          <div className="p-4 bg-gradient-to-r from-[#14B8A6]/15 via-teal-500/10 to-amber-500/10 border border-[#14B8A6]/30 rounded-2xl space-y-2 shadow-sm">
            <div className="flex items-start gap-2.5">
              <Sparkles size={18} className="text-[#14B8A6] shrink-0 mt-0.5" />
              <div className="space-y-1 text-xs text-gray-200 leading-relaxed">
                <p className="font-bold text-white text-sm">
                  ✨ 7-Day Full-Access Free Trial
                </p>
                <p className="text-gray-300">
                  Sign in or create an account with Google or Email to instantly unlock all financial tools, invoice tracking, and AI Co-Founder insights. No credit card required.
                </p>
              </div>
            </div>
          </div>

          {/* ZERO-TELEMETRY & PRIVACY ARCHITECTURE DISCLOSURE */}
          <div className="p-3.5 bg-[#0C0E0F] border border-[#14B8A6]/30 rounded-2xl space-y-2.5 text-[11px] text-gray-300 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-bold text-xs">
                <ShieldCheck size={15} className="text-[#14B8A6]" />
                <span>Privacy-First Architecture &amp; Data Security</span>
              </div>
              <span className="text-[10px] font-mono text-[#14B8A6] bg-[#14B8A6]/10 px-2 py-0.5 rounded border border-[#14B8A6]/20 font-bold">
                Zero Scrape
              </span>
            </div>

            <div className="space-y-1.5 text-[11px] text-gray-300 leading-relaxed font-light">
              <div className="flex items-start gap-1.5">
                <span className="text-[#14B8A6] font-bold shrink-0">•</span>
                <span>
                  <strong className="text-white font-medium">Private Workspace:</strong> AzuraOS does <em>not</em> scrape, crawl, sell, or train internal AI models on your private invoices, client agreements, or ledger data.
                </span>
              </div>
              <div className="flex items-start gap-1.5">
                <span className="text-teal-400 font-bold shrink-0">•</span>
                <span>
                  <strong className="text-white font-medium">Bot &amp; Crawler Shield:</strong> Secure authentication isolates your workspace so search crawlers and public AI bots cannot access your confidential numbers.
                </span>
              </div>
              <div className="flex items-start gap-1.5">
                <span className="text-amber-400 font-bold shrink-0">•</span>
                <span>
                  <strong className="text-white font-medium">8 Multi-Provider AI Engines (BYOK):</strong> AI features are strictly on-demand. When you choose to consult the AI or configure Bring-Your-Own-Key (BYOK), prompts are dispatched directly to your chosen sovereign provider (Google Gemini, OpenAI GPT-4o, Anthropic Claude, Groq, DeepSeek, Mistral, Perplexity, or OpenRouter) under their official enterprise developer privacy terms with zero model training.
                </span>
              </div>
            </div>
          </div>

          {/* MANDATORY CONSENT CHECKBOX & DISCLAIMER */}
          <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl space-y-2">
            <div className="flex items-start gap-2.5">
              <input
                type="checkbox"
                id="auth-terms-checkbox"
                checked={agreedToTerms}
                onChange={(e) => {
                  setAgreedToTerms(e.target.checked);
                  if (e.target.checked && error?.includes('Terms')) setError(null);
                }}
                className="w-4 h-4 rounded border-[#2A2F31] bg-[#151819] text-[#14B8A6] focus:ring-[#14B8A6] focus:ring-offset-0 cursor-pointer accent-[#14B8A6] shrink-0 mt-0.5"
              />
              <label htmlFor="auth-terms-checkbox" className="text-[11px] text-gray-300 cursor-pointer select-none leading-normal">
                I accept the{' '}
                {onOpenTerms ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onOpenTerms();
                    }}
                    className="text-[#14B8A6] hover:underline font-bold transition-colors cursor-pointer"
                  >
                    AzuraOS Terms of Service
                  </button>
                ) : (
                  <span className="text-[#14B8A6] font-bold">AzuraOS Terms of Service</span>
                )}{' '}
                and{' '}
                {onOpenPrivacy ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onOpenPrivacy();
                    }}
                    className="text-[#14B8A6] hover:underline font-bold transition-colors cursor-pointer"
                  >
                    Privacy Policy
                  </button>
                ) : (
                  <span className="text-[#14B8A6] font-bold">Privacy Policy</span>
                )}
                . I understand AzuraOS provides financial visibility tools and not financial advice.
              </label>
            </div>

            {/* MANDATORY AGE & COMMERCIAL CAPACITY GATE (18+) */}
            <div className="flex items-start gap-2.5 pt-2 border-t border-[#1F2426]">
              <input
                type="checkbox"
                id="auth-age-gate-checkbox"
                checked={isAgeVerified}
                onChange={(e) => {
                  setIsAgeVerified(e.target.checked);
                  if (e.target.checked && error?.includes('18')) setError(null);
                }}
                className="w-4 h-4 rounded border-[#2A2F31] bg-[#151819] text-[#14B8A6] focus:ring-[#14B8A6] focus:ring-offset-0 cursor-pointer accent-[#14B8A6] shrink-0 mt-0.5"
              />
              <label htmlFor="auth-age-gate-checkbox" className="text-[11px] text-gray-300 cursor-pointer select-none leading-normal">
                <strong className="text-white">Age &amp; Legal Capacity Gate (18+):</strong> I certify that I am at least <strong>18 years of age</strong> and possess full legal capacity to enter into binding commercial contracts on behalf of my business.
              </label>
            </div>
            
            <div className="pt-1 flex items-center justify-between border-t border-[#1F2426] text-[10px] text-gray-400">
              <span>Plain-English terms. 18+ verification enforced.</span>
              <div className="flex items-center gap-2">
                {onOpenTerms && (
                  <button
                    type="button"
                    onClick={onOpenTerms}
                    className="text-[#14B8A6] hover:text-teal-300 font-bold transition-colors cursor-pointer flex items-center gap-0.5"
                  >
                    <span>Terms</span>
                    <ExternalLink size={9} />
                  </button>
                )}
                {onOpenTerms && onOpenPrivacy && <span>•</span>}
                {onOpenPrivacy && (
                  <button
                    type="button"
                    onClick={onOpenPrivacy}
                    className="text-[#14B8A6] hover:text-teal-300 font-bold transition-colors cursor-pointer flex items-center gap-0.5"
                  >
                    <span>Privacy</span>
                    <ExternalLink size={9} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* SUCCESS NOTICE BANNER (E.G. PASSWORD RESET DISPATCHED) */}
          {successNotice && (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl text-emerald-300 text-xs space-y-2 animate-fade-in shadow-inner">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 size={18} className="shrink-0 mt-0.5 text-emerald-400" />
                <div className="space-y-1">
                  <span className="font-bold text-emerald-200 block text-xs">Email Dispatched Successfully</span>
                  <p className="leading-relaxed text-gray-300">{successNotice}</p>
                </div>
              </div>
            </div>
          )}

          {/* GLOBAL AUTH ERROR BANNER */}
          {error && (
            <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-rose-300 text-xs space-y-2.5 animate-fade-in shadow-inner">
              <div className="flex items-start gap-2.5">
                <AlertCircle size={18} className="shrink-0 mt-0.5 text-rose-400" />
                <div className="space-y-1">
                  <span className="font-bold text-rose-200 block text-xs">Authentication Notice</span>
                  <p className="leading-relaxed text-gray-300">{error}</p>
                </div>
              </div>
              <div className="pt-2 border-t border-rose-500/20 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {emailAuthMode === 'signup' ? (
                    <button
                      type="button"
                      onClick={() => {
                        setEmailAuthMode('signin');
                        setError(null);
                      }}
                      className="px-3 py-1.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl text-xs transition-all cursor-pointer shadow-md"
                    >
                      Switch to Sign In
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setEmailAuthMode('signup');
                        setError(null);
                      }}
                      className="px-3 py-1.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl text-xs transition-all cursor-pointer shadow-md"
                    >
                      Switch to Create Account
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMethod('google');
                      setError(null);
                    }}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-xs transition-all cursor-pointer"
                  >
                    Use Google Sign-In
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const founderEmail = email.trim() || 'founder@azuraos.app';
                    showToast(`Local Encrypted Vault Session active for ${founderEmail}`);
                    onSuccess(founderEmail);
                    onClose();
                  }}
                  className="px-3.5 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 font-bold rounded-xl text-xs transition-all cursor-pointer border border-gray-600 flex items-center gap-1.5 active:scale-95"
                >
                  <ShieldCheck size={14} className="text-[#14B8A6]" />
                  <span>Launch Local Vault</span>
                </button>
              </div>
            </div>
          )}

          {/* PRIMARY METHOD SELECTOR: GOOGLE vs EMAIL */}
          <div className="space-y-4">
            <div className="flex bg-[#0C0E0F] p-1 rounded-xl border border-[#2A2F31]">
              <button
                type="button"
                onClick={() => { setAuthMethod('google'); setError(null); }}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                  authMethod === 'google' ? 'bg-[#1F2426] text-white shadow-sm' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Sparkles size={13} className="text-[#14B8A6]" />
                <span>Google Sign-In</span>
              </button>
              <button
                type="button"
                onClick={() => { setAuthMethod('email'); setError(null); }}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                  authMethod === 'email' ? 'bg-[#14B8A6] text-black shadow-md font-extrabold' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Mail size={13} />
                <span>Email &amp; Password</span>
              </button>
            </div>

            {/* TAB CONTENT A: GOOGLE AUTH */}
            {authMethod === 'google' && (
              <div className="bg-[#0C0E0F] border border-[#14B8A6]/30 rounded-2xl p-4 sm:p-5 space-y-3.5 shadow-lg shadow-[#14B8A6]/5 animate-fade-in">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCheck size={16} className="text-[#14B8A6]" />
                    <span className="text-xs font-bold text-white">Google 1-Click Launch</span>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                    Fastest
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading || !agreedToTerms || !isAgeVerified}
                  className="w-full py-3.5 px-4 bg-white hover:bg-gray-100 text-black font-extrabold text-xs rounded-xl transition-all flex items-center justify-center gap-3 cursor-pointer shadow-md active:scale-98 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    />
                  </svg>
                  <span>Continue with Google Sign-In</span>
                </button>

                <p className="text-[10px] text-gray-400 text-center leading-relaxed">
                  Sign in with your Google account to instantly launch your 7-Day Free Trial.
                </p>
              </div>
            )}

            {/* TAB CONTENT B: EMAIL & PASSWORD WITH SIGN IN / SIGN UP TOGGLE */}
            {authMethod === 'email' && (
              <div className="bg-[#0C0E0F]/90 border border-[#2A2F31] rounded-2xl p-4 sm:p-5 space-y-4 animate-fade-in shadow-xl">
                
                {/* Mode Toggle: Create Account vs Sign In */}
                <div className="flex bg-[#151819] p-1 rounded-xl border border-[#2A2F31]">
                  <button
                    type="button"
                    onClick={() => { setEmailAuthMode('signup'); setError(null); }}
                    className={`flex-1 py-1.5 text-[11px] font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                      emailAuthMode === 'signup' 
                        ? 'bg-[#14B8A6] text-black shadow-sm font-extrabold' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <UserPlus size={13} />
                    <span>Create Account (Sign Up)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEmailAuthMode('signin'); setError(null); }}
                    className={`flex-1 py-1.5 text-[11px] font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                      emailAuthMode === 'signin' 
                        ? 'bg-[#1F2426] text-white shadow-sm font-extrabold' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <LogIn size={13} />
                    <span>Sign In</span>
                  </button>
                </div>

                <div className="space-y-1">
                  <h3 className="text-xs font-bold text-white flex items-center gap-1.5">
                    {emailAuthMode === 'signup' ? (
                      <>
                        <UserPlus size={14} className="text-[#14B8A6]" />
                        <span>Create New AzuraOS Account</span>
                      </>
                    ) : (
                      <>
                        <Lock size={14} className="text-[#14B8A6]" />
                        <span>Sign In to Your Workspace</span>
                      </>
                    )}
                  </h3>
                  <p className="text-[11px] text-gray-400 leading-relaxed">
                    {emailAuthMode === 'signup' 
                      ? 'Register with your email and password to instantly start your 7-Day Free Trial.'
                      : 'Enter your account credentials to log in to your workspace.'}
                  </p>
                </div>

                <form onSubmit={handleEmailAuth} className="space-y-3.5 pt-1">
                  <div>
                    <label className="block text-[10px] uppercase font-bold text-gray-400 mb-1">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-2.5 text-gray-500" />
                      <input
                        type="email"
                        required
                        placeholder="founder@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-[#151819] border border-[#2A2F31] focus:border-[#14B8A6] rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder:text-gray-600 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-[10px] uppercase font-bold text-gray-400">
                        Password {emailAuthMode === 'signup' && <span className="text-gray-500 font-normal">(min 6 chars)</span>}
                      </label>
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={loading}
                        className="text-[11px] text-[#14B8A6] hover:underline font-semibold cursor-pointer transition-colors"
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-2.5 text-gray-500" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={6}
                        placeholder="••••••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-[#151819] border border-[#2A2F31] focus:border-[#14B8A6] rounded-xl pl-9 pr-10 py-2 text-xs text-white placeholder:text-gray-600 focus:outline-none transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-300 cursor-pointer"
                      >
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !agreedToTerms || (emailAuthMode === 'signup' && !isAgeVerified)}
                    className="w-full py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed active:scale-98"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>
                          {emailAuthMode === 'signup' ? 'Create Account & Start Trial' : 'Sign In to Workspace'}
                        </span>
                        <ArrowRight size={14} />
                      </>
                    )}
                  </button>

                  <div className="text-center pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setEmailAuthMode(emailAuthMode === 'signup' ? 'signin' : 'signup');
                        setError(null);
                      }}
                      className="text-[11px] text-[#14B8A6] hover:underline font-medium transition-colors cursor-pointer"
                    >
                      {emailAuthMode === 'signup'
                        ? 'Already have an account? Sign In'
                        : 'Need an account? Create one for a 7-Day Free Trial'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Privacy & Security footer */}
          <div className="pt-2 border-t border-[#2A2F31]/50 text-center text-[10px] text-gray-500 flex items-center justify-center gap-1.5 flex-wrap">
            <Cloud size={12} className="text-[#14B8A6]" />
            <span>End-to-end user isolation guaranteed by Firebase Security Rules ({getDisplayProjectId()}).</span>
          </div>

        </div>
      </div>
    </div>
  );
}

export default AuthModal;


